import React, { useMemo } from 'react';
import { Box, Checkbox, FormControlLabel, Grid, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SchedulerConfigurationData } from '../../../../api';
import { useLanguage } from '../../../../common/GeneralUtils';
import {
  localizedLabel,
  maskHasIndex,
  resourceDisplayName,
  subtractMask,
  unionMasks,
} from '../../../../utils/maskUtils';

export enum SchedulerGridDisplayMode {
  FULL = 'full', // Full interactive mode with labels and help text
  COMPACT = 'compact', // Compact mode for popover display
  INLINE = 'inline', // Ultra-compact inline mode for unavailability box
}

interface SchedulerResourceGridProps {
  schedulerConfig: SchedulerConfigurationData;
  blockedMask: string;
  onChange?: (mask: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  displayMode?: SchedulerGridDisplayMode;
}

export const SchedulerResourceGrid: React.FC<SchedulerResourceGridProps> = ({
  schedulerConfig,
  blockedMask,
  onChange,
  disabled = false,
  readOnly = false,
  displayMode = SchedulerGridDisplayMode.FULL,
}) => {
  const language = useLanguage();
  const { t } = useTranslation();

  const resourceCount = schedulerConfig.resource_count;
  const currentMask = blockedMask.padEnd(resourceCount, '0').substring(0, resourceCount);

  /**
   * Resources grouped by the area they sit in, so blocking reads as "close the yard" rather than
   * "tick tables 24 to 34". A config without areas keeps the single flat grid.
   */
  const areaGroups = useMemo(() => {
    const areas = schedulerConfig.areas ?? [];
    const all = Array.from({ length: resourceCount }, (unused, index) => index);
    if (areas.length === 0) {
      return [{ key: schedulerConfig.id, label: '', mask: '', indexes: all }];
    }
    const grouped = areas.map((area) => ({
      key: area.id,
      label: localizedLabel(area.label, language, area.id),
      mask: area.mask,
      indexes: all.filter((index) => maskHasIndex(area.mask, index)),
    }));
    const ungrouped = all.filter((index) => !areas.some((area) => maskHasIndex(area.mask, index)));
    return ungrouped.length > 0
      ? [...grouped, { key: `${schedulerConfig.id}-other`, label: '', mask: '', indexes: ungrouped }]
      : grouped;
  }, [schedulerConfig, resourceCount, language]);

  const toggleResourceBlock = (position: number) => {
    if (disabled || readOnly || !onChange) return;

    const maskArray = currentMask.split('');
    const reversedPosition = resourceCount - 1 - position;
    maskArray[reversedPosition] = maskArray[reversedPosition] === '1' ? '0' : '1';
    const newMask = maskArray.join('');
    onChange(newMask);
  };

  const buildSingle = (index: number) =>
    Array.from({ length: resourceCount }, (unused, position) =>
      position === resourceCount - 1 - index ? '1' : '0',
    ).join('');

  const setAreaBlocked = (areaMask: string, indexes: number[], blocked: boolean) => {
    if (disabled || readOnly || !onChange) return;
    const target = areaMask || indexes.reduce((mask, index) => unionMasks(mask, buildSingle(index)), '');
    const next = blocked ? unionMasks(currentMask, target) : subtractMask(currentMask, target);
    onChange(next.padStart(resourceCount, '0').slice(-resourceCount));
  };

  const blockedCountIn = (indexes: number[]) =>
    indexes.filter((index) => currentMask[resourceCount - 1 - index] === '1').length;

  const renderResourcePositions = (indexes: number[]) => {
    const positions = [];
    for (const i of indexes) {
      const reversedPosition = resourceCount - 1 - i;
      const isBlocked = currentMask[reversedPosition] === '1';
      positions.push(
        <Tooltip key={i} title={resourceDisplayName(i, schedulerConfig, language)}>
          <Box
            onClick={() => toggleResourceBlock(i)}
            sx={{
              width:
                displayMode === SchedulerGridDisplayMode.INLINE
                  ? 16
                  : displayMode === SchedulerGridDisplayMode.COMPACT
                  ? 32
                  : 40,
              height:
                displayMode === SchedulerGridDisplayMode.INLINE
                  ? 16
                  : displayMode === SchedulerGridDisplayMode.COMPACT
                  ? 32
                  : 40,
              border: displayMode === SchedulerGridDisplayMode.INLINE ? '1px solid' : '2px solid',
              borderColor: 'mb.control',
              borderRadius: displayMode === SchedulerGridDisplayMode.INLINE ? '2px' : '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled || readOnly ? 'default' : 'pointer',
              backgroundColor: isBlocked ? 'error.main' : 'primary.main',
              color: 'common.white',
              fontWeight: 'bold',
              fontSize:
                displayMode === SchedulerGridDisplayMode.INLINE
                  ? '8px'
                  : displayMode === SchedulerGridDisplayMode.COMPACT
                  ? '12px'
                  : '14px',
              '&:hover':
                disabled || readOnly
                  ? {}
                  : {
                      opacity: 0.8,
                    },
            }}>
            {displayMode === SchedulerGridDisplayMode.INLINE ? '' : i + 1}
          </Box>
        </Tooltip>,
      );
    }
    return positions;
  };

  if (displayMode === SchedulerGridDisplayMode.INLINE) {
    return (
      <Grid container alignItems="center" gap={0.25} flexWrap="nowrap">
        <Typography variant="caption" sx={{ fontSize: '11px', marginInlineEnd: '4px' }}>
          {schedulerConfig.label[language] || schedulerConfig.label.en || `Config ${schedulerConfig.id}`}:
        </Typography>
        <Grid container gap={0.25} flexWrap="nowrap">
          {renderResourcePositions(areaGroups.flatMap((group) => group.indexes))}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      flexDirection="column"
      gap={displayMode === SchedulerGridDisplayMode.COMPACT ? 1 : 2}
      mb={displayMode === SchedulerGridDisplayMode.COMPACT ? 1 : 2}>
      <Typography
        variant={displayMode === SchedulerGridDisplayMode.COMPACT ? 'body2' : 'subtitle1'}
        fontWeight={displayMode === SchedulerGridDisplayMode.COMPACT ? '600' : '400'}>
        {schedulerConfig.label[language] || schedulerConfig.label.en || `Config ${schedulerConfig.id}`}
      </Typography>

      {areaGroups.map((group) => {
        const blockedInArea = blockedCountIn(group.indexes);
        const allBlocked = blockedInArea === group.indexes.length;

        return (
          <Grid key={group.key} container flexDirection="column" wrap="nowrap" gap={0.5}>
            {group.label && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allBlocked}
                    indeterminate={blockedInArea > 0 && !allBlocked}
                    disabled={disabled || readOnly}
                    onChange={() => setAreaBlocked(group.mask, group.indexes, !allBlocked)}
                    inputProps={{
                      'aria-label': t('calendar.unavailability_dialog.toggle_area', { area: group.label }),
                    }}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    {group.label}{' '}
                    <Typography component="span" variant="caption" color="text.secondary" dir="ltr">
                      {blockedInArea}/{group.indexes.length}
                    </Typography>
                  </Typography>
                }
                sx={{ marginInlineStart: 0 }}
              />
            )}
            <Grid
              container
              gap={displayMode === SchedulerGridDisplayMode.COMPACT ? 0.5 : 1}
              flexWrap="wrap"
              maxWidth={displayMode === SchedulerGridDisplayMode.COMPACT ? 300 : 400}>
              {renderResourcePositions(group.indexes)}
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
};
